// Client/src/pages/digi/Invoice/Components/ArchiveTab.jsx
//
// Archived invoices (archive replaces delete — docs/superpowers/specs/
// 2026-11-04-invoice-archive-design.md). Staff see what their invoice list would
// show them; vendors see their own. Each row is one archived invoice with who
// archived it, when and why; a row opens its lines and files. The company
// account, auditors and lead technical staff can restore it.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Paper } from "@mui/material";
import { Input, Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import {
  getArchivedInvoices,
  getArchivedInvoice,
  restoreArchivedInvoice,
  isArchiveAdmin,
  formatCurrency,
  itemLabel,
  resolveRateType,
  flatAmountOf,
} from "./Api";

const TZ = "America/Edmonton";
const AMBER = "#B45309";

const STATUS_TONE = {
  Pending: { bg: "#FFFBEB", fg: "#92400E", border: "#FDE68A" },
  Declined: { bg: "#FEF2F2", fg: "#B91C1C", border: "#FECACA" },
};

const StatusPill = ({ status }) => {
  const t = STATUS_TONE[status] || { bg: "#F1F5F9", fg: "#475569", border: "#E2E8F0" };
  return (
    <span style={{
      display: "inline-flex", borderRadius: 20, padding: "2px 9px", fontSize: "0.72rem", fontWeight: 700,
      background: t.bg, color: t.fg, border: `1px solid ${t.border}`, whiteSpace: "nowrap",
    }}>{status || "—"}</span>
  );
};

const rdOf = (row) => (row.invoiceNo != null ? `RD ${row.invoiceNo}` : "Invoice");
const when = (d) => (d ? moment.tz(d, TZ).format("MMM D, YYYY h:mm a") : "—");

// "6 h 30 m × $30.00" for hourly lines, the flat charge otherwise.
const workAmount = (line) => {
  if (resolveRateType(line) === "flat") return formatCurrency(flatAmountOf(line));
  const h = Number(line.hours) || 0;
  const m = Number(line.minutes) || 0;
  const time = m ? `${h} h ${m} m` : `${h} h`;
  return `${time} × ${formatCurrency(line.amountPerHour)}`;
};

const lineWhere = (line) => {
  const p = (line.properties && line.properties[0]) || {};
  const title = p.title || "";
  const building = p.buildingName && p.buildingName !== title ? p.buildingName : "";
  return [title, building].filter(Boolean).join(" · ") || "—";
};

const lineWork = (line) => {
  const p = (line.properties && line.properties[0]) || {};
  return [p.jobDescription, line.comment].filter(Boolean).join(" — ") || line.title || "—";
};

const th = { padding: "9px 10px", fontSize: "0.72rem", fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.4, textAlign: "left", whiteSpace: "nowrap", borderBottom: "1px solid #E2E8F0" };
const td = { padding: "10px", fontSize: "0.84rem", color: "#1E293B", verticalAlign: "top", borderBottom: "1px solid #F1F5F9" };

const Detail = ({ detail }) => {
  if (!detail || detail.loading) {
    return (
      <div style={{ padding: 14, color: "#64748B", fontSize: "0.84rem" }}>
        <Spinner size="sm" /> Loading…
      </div>
    );
  }
  if (detail.error) return <div style={{ padding: 14, color: "#B91C1C", fontSize: "0.84rem" }}>{detail.error}</div>;
  const data = detail.data;
  return (
    <div style={{ padding: "6px 14px 14px", background: "#FAFAF9" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff", border: "1px solid #E7E5E4", borderRadius: 8 }}>
        <thead>
          <tr>
            <th style={th}>Item</th>
            <th style={th}>Property</th>
            <th style={th}>Unit</th>
            <th style={th}>Work</th>
            <th style={th}>Hours / amount</th>
            <th style={{ ...th, textAlign: "right" }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {(data.lines || []).map((line) => (
            <tr key={line._id}>
              <td style={td}>{itemLabel(line) || "—"}</td>
              <td style={td}>{lineWhere(line)}</td>
              <td style={td}>{(line.properties && line.properties[0] && line.properties[0].unitNo) || "—"}</td>
              <td style={td}>{lineWork(line)}</td>
              <td style={td}>{workAmount(line)}</td>
              <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>{formatCurrency(line.total, line.currency)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: 10, fontSize: "0.82rem", color: "#475569" }}>
        <strong>Files: </strong>
        {(data.files || []).length === 0 ? "none" : (data.files || []).map((f, i) => (
          <span key={f.key}>
            {i > 0 && " · "}
            {f.url ? (
              <a href={f.url} target="_blank" rel="noopener noreferrer">{f.name}</a>
            ) : (
              <span title="This file could not be opened">{f.name}</span>
            )}
          </span>
        ))}
      </div>
    </div>
  );
};

export const ArchiveTab = ({ user }) => {
  const isVendor = String(user?.role || "").toLowerCase() === "vendor";
  const canRestore = isArchiveAdmin(user);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [month, setMonth] = useState("");
  const [vendor, setVendor] = useState("");
  const [openKey, setOpenKey] = useState(null);
  const [details, setDetails] = useState({});
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getArchivedInvoices({ search: search || undefined, month: month || undefined });
      setRows(Array.isArray(data) ? data : []);
      setDetails({});
      setOpenKey(null);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [search, month]);

  useEffect(() => { load(); }, [load]);

  // Search applies 400 ms after typing stops (or on Enter).
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchText.trim()), 400);
    return () => clearTimeout(t);
  }, [searchText]);

  const vendors = useMemo(
    () => Array.from(new Set(rows.map((r) => r.vendorName).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [rows]
  );
  const shown = useMemo(() => (vendor ? rows.filter((r) => r.vendorName === vendor) : rows), [rows, vendor]);

  const toggle = async (row) => {
    if (openKey === row.key) {
      setOpenKey(null);
      return;
    }
    setOpenKey(row.key);
    if (details[row.key]?.data) return;
    setDetails((d) => ({ ...d, [row.key]: { loading: true } }));
    try {
      const data = await getArchivedInvoice(row.lineIds[0]);
      setDetails((d) => ({ ...d, [row.key]: { data } }));
    } catch (e) {
      setDetails((d) => ({ ...d, [row.key]: { error: e?.message || "Could not load this invoice" } }));
    }
  };

  const restore = async (row, e) => {
    e.stopPropagation();
    const res = await Swal.fire({
      titleText: `Restore invoice ${rdOf(row)}?`,
      text: `It returns to the Invoices list as ${row.statusAtArchive || "it was"}.`,
      showCancelButton: true,
      confirmButtonText: "Restore",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#4F46E5",
      width: 420,
    });
    if (!res.isConfirmed) return;
    setBusyKey(row.key);
    try {
      await restoreArchivedInvoice(row.lineIds);
      toast.success("Restored — back in the Invoices list");
      await load();
    } catch {
      // restoreArchivedInvoice already showed the server's reason.
    } finally {
      setBusyKey(null);
    }
  };

  const columns = 8 + (canRestore ? 1 : 0);

  return (
    <Paper elevation={0} style={{ border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", padding: "12px 14px", borderBottom: "1px solid #E2E8F0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flex: "1 1 260px", minWidth: 220 }}>
          <FeatherIcon icon="search" size={15} color="#94A3B8" />
          <Input
            type="search"
            bsSize="sm"
            placeholder={isVendor ? "Search RD #, title or reason" : "Search RD #, vendor or reason"}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") setSearch(searchText.trim()); }}
            aria-label="Search archived invoices"
          />
        </div>
        {!isVendor && (
          <Input
            type="select"
            bsSize="sm"
            value={vendor}
            onChange={(e) => setVendor(e.target.value)}
            style={{ width: 200 }}
            aria-label="Filter by vendor"
          >
            <option value="">All vendors</option>
            {vendors.map((v) => <option key={v} value={v}>{v}</option>)}
          </Input>
        )}
        <Input
          type="month"
          bsSize="sm"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          style={{ width: 170 }}
          aria-label="Archived in month"
          title="Archived in month"
        />
        {(searchText || month || vendor) && (
          <button
            type="button"
            className="btn btn-sm btn-light"
            onClick={() => { setSearchText(""); setSearch(""); setMonth(""); setVendor(""); }}
          >
            Clear
          </button>
        )}
        <span style={{ marginLeft: "auto", fontSize: "0.8rem", color: "#64748B" }}>
          {loading ? "" : `${shown.length} archived invoice${shown.length === 1 ? "" : "s"}`}
        </span>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead style={{ background: "#F8FAFC" }}>
            <tr>
              <th style={th}>Invoice</th>
              <th style={th}>Vendor</th>
              <th style={th}>Properties</th>
              <th style={{ ...th, textAlign: "right" }}>Total</th>
              <th style={th}>Status when archived</th>
              <th style={th}>Archived by</th>
              <th style={th}>Archived on</th>
              <th style={th}>Reason</th>
              {canRestore && <th style={th} aria-label="Actions" />}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td style={{ ...td, textAlign: "center", color: "#64748B" }} colSpan={columns}><Spinner size="sm" /> Loading…</td></tr>
            )}
            {!loading && shown.length === 0 && (
              <tr>
                <td style={{ ...td, textAlign: "center", color: "#64748B", padding: 28 }} colSpan={columns}>
                  {searchText || month || vendor ? "No archived invoices match." : "Nothing archived yet."}
                </td>
              </tr>
            )}
            {!loading && shown.map((row) => {
              const open = openKey === row.key;
              return [
                <tr
                  key={row.key}
                  onClick={() => toggle(row)}
                  style={{ cursor: "pointer", background: open ? "#FFFBEB" : undefined }}
                  aria-expanded={open}
                >
                  <td style={td}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <FeatherIcon icon={open ? "chevron-down" : "chevron-right"} size={14} color="#94A3B8" />
                      <strong>{rdOf(row)}</strong>
                    </div>
                    {row.title && <div style={{ fontSize: "0.76rem", color: "#64748B", marginLeft: 20 }}>{row.title}</div>}
                    <div style={{ fontSize: "0.72rem", color: "#94A3B8", marginLeft: 20 }}>
                      {row.lineCount} item{row.lineCount === 1 ? "" : "s"}
                    </div>
                  </td>
                  <td style={td}>{row.vendorName || "—"}</td>
                  <td style={{ ...td, maxWidth: 220 }}>{row.properties.join(", ") || "—"}</td>
                  <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>{formatCurrency(row.total, row.currency)}</td>
                  <td style={td}><StatusPill status={row.statusAtArchive} /></td>
                  <td style={td}>{row.archive?.by?.displayName || "—"}</td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>{when(row.archive?.at)}</td>
                  <td style={{ ...td, maxWidth: 280 }}>
                    {row.archive?.reason || "—"}
                    {row.archive?.via === "legacy-delete" && (
                      <div style={{ fontSize: "0.72rem", color: "#94A3B8" }}>From the old Delete button</div>
                    )}
                  </td>
                  {canRestore && (
                    <td style={{ ...td, whiteSpace: "nowrap" }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={(e) => restore(row, e)}
                        disabled={busyKey === row.key}
                      >
                        {busyKey === row.key ? <Spinner size="sm" /> : <FeatherIcon icon="rotate-ccw" size={13} />} Restore
                      </button>
                    </td>
                  )}
                </tr>,
                open && (
                  <tr key={`${row.key}-detail`}>
                    <td colSpan={columns} style={{ padding: 0, borderBottom: `2px solid ${AMBER}22` }}>
                      <Detail detail={details[row.key]} />
                    </td>
                  </tr>
                ),
              ];
            })}
          </tbody>
        </table>
      </div>
    </Paper>
  );
};
